import { nanoid } from 'nanoid';

import { dynamicImport } from '@canvas/dynamic-import';

let worker: Worker;

const isCompatMode = !!process.env.VITE_COMPAT;
const isLocalMode = !!process.env.VITE_LOCAL;

// When we fire an analytics event, we wait for the validator worker to respond.
// If it doesn't respond within this time, we assume the worker is not available
// and we just go ahead and send the analytics event.
const ANALYTICS_WORKER_TIMEOUT = 30000;
// On local dev, we don't want to wait for the worker to respond.
const ANALYTICS_LOCAL_TIMEOUT = 1000;

/**
 * @privateRemarks Each branch must be a literal specifier: bundlers cannot statically analyse a
 * dynamic import whose specifier is an expression.
 */
const getWorkerUrl = async (shouldUseClassicMode: boolean): Promise<string> => {
  if (shouldUseClassicMode) {
    return (
      await dynamicImport(() => import('./workers/validate-analytics-worker.es.js?url'))
    ).default;
  }

  return (await dynamicImport(() => import('./validate-analytics.worker?url'))).default;
};

export const validateAnalytics = async (
  eventType: 'ui' | 'track' | 'screen' | 'operational',
  eventPayload: Record<string, unknown>,
): Promise<{ success: boolean; error: Error | null }> => {
  // Workers cannot be used in module mode in every browser.
  // Hence, in production, we fallback on classic mode.
  const shouldUseClassicMode = isCompatMode || !isLocalMode;

  try {
    worker =
      worker ||
      new Worker(await getWorkerUrl(shouldUseClassicMode), {
        type: shouldUseClassicMode ? 'classic' : 'module',
      });

    const timeout = new Promise<{ success: boolean; error: Error | null }>((resolve) => {
      setTimeout(
        () => {
          resolve({
            success: false,
            error: new Error('Analytics worker timed out'),
          });
        },
        isLocalMode ? ANALYTICS_LOCAL_TIMEOUT : ANALYTICS_WORKER_TIMEOUT,
      );
    });

    const sendValidation = new Promise<{ success: boolean; error: Error | null }>(
      (resolve) => {
        const id = nanoid();

        worker.postMessage({
          method: 'validate',
          id,
          arguments: [{ eventType, ...eventPayload }],
        });

        const onMessage = async (e: MessageEvent) => {
          if (e.data.id) {
            resolve(e.data.result);
            worker.removeEventListener('message', onMessage);
          }
        };

        worker.addEventListener('message', onMessage);
      },
    );

    return Promise.race([timeout, sendValidation]);
  } catch (error) {
    return {
      success: false,
      error: new Error(
        `Analytics worker failed to initialize: ${
          error instanceof Error ? error.message : String(error)
        }`,
      ),
    };
  }
};
